import { useState } from "react";
import { toast } from "sonner";
import { GalleryHorizontal, Download, Loader2, RefreshCw } from "lucide-react";
import { API, http, errMsg, downloadFile } from "../../lib/api";
import { SchedulePosts } from "./SchedulePosts";

const More = ({ bare, children }) => (bare ? (
  <details data-testid="carousel-more" className="mt-4 group"><summary className="cursor-pointer text-xs text-stone-500 hover:text-[#2B2A26]">Stories, reel &amp; post schedule</summary>{children}</details>
) : children);

export const CarouselCard = ({ kit, reload, bare }) => {
  const [busy, setBusy] = useState(false);
  const car = kit.carousel;
  const building = busy || kit.carousel_building;
  const v = car ? encodeURIComponent(car.created_at) : "";
  const make = async () => {
    setBusy(true);
    try { await http.post(`/kits/${kit.id}/carousel`); toast.message("Designing carousel, stories and reel — about a minute"); reload(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="carousel-card" className={bare ? "pt-6 mt-6 border-t border-stone-200" : "rounded-xl border border-stone-200 bg-white p-6 lg:col-span-2"}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="eyebrow flex items-center gap-2"><GalleryHorizontal size={14} /> Instagram carousel</div>
        <div className="flex gap-2">
          {car && <button data-testid="carousel-download" onClick={() => downloadFile(`/kits/${kit.id}/carousel.zip`, "carousel.zip").catch((e) => toast.error(errMsg(e)))}
            className="inline-flex items-center gap-1.5 rounded-sm bg-[#2B2A26] px-3 py-1.5 text-xs font-bold text-white"><Download size={13} /> Download carousel, stories, reel + captions</button>}
          <button data-testid="carousel-generate" onClick={make} disabled={building || !kit.images?.cover} title={kit.images?.cover ? "" : "Add a cover photo first"}
            className="inline-flex items-center gap-1.5 rounded-sm border border-stone-300 px-3 py-1.5 text-xs hover:bg-stone-100 disabled:opacity-40">
            {building ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} {building ? "Designing slides & reel…" : car ? "Rebuild" : "Create carousel"}
          </button>
        </div>
      </div>
      {kit.carousel_error && <p data-testid="carousel-error" className="text-sm text-red-600 mt-3">{kit.carousel_error}</p>}
      {!car && <p className="text-sm text-stone-500 mt-3">Built automatically when the kit finishes: five 1080×1350 carousel slides (cover, three highlight venues — Founder Verified first — and a “Link in bio” close) plus matching 1080×1920 Story frames with a “Shop now” link-sticker spot and a 15-second Reel.</p>}
      {car && <div className="mt-4 flex gap-3 overflow-x-auto pb-2">
        {car.slides.map((s, i) => (
          <figure key={s.path} className="shrink-0 w-44">
            <img data-testid={`carousel-slide-${i + 1}`} src={`${API}/kits/${kit.id}/carousel/${i + 1}.jpg?v=${v}`} alt={`Slide ${i + 1}`} className="w-44 h-55 aspect-[4/5] object-cover rounded-md border border-stone-200" />
            <figcaption className="text-[11px] text-stone-500 mt-1 leading-snug">{i + 1}. {s.caption}</figcaption>
          </figure>
        ))}
      </div>}
      <More bare={bare}>
      {car?.stories?.length > 0 && <>
        <div className="eyebrow mt-5">Story frames · 1080×1920 · add a “Shop now” link sticker over the dashed box</div>
        <div className="mt-3 flex gap-3 overflow-x-auto pb-2">
          {car.stories.map((s, i) => (
            <img key={s.path} data-testid={`story-slide-${i + 1}`} src={`${API}/kits/${kit.id}/story/${i + 1}.jpg?v=${v}`} alt={`Story ${i + 1}`} className="shrink-0 w-32 aspect-[9/16] object-cover rounded-md border border-stone-200" />
          ))}
        </div>
      </>}
      {car?.reel && <>
        <div className="eyebrow mt-5">Reel · 15 seconds · 5 scenes × 3s (6 beats at 120 bpm) · silent — add a trending audio track in Instagram</div>
        <video data-testid="reel-video" controls playsInline preload="metadata" src={`${API}/kits/${kit.id}/reel.mp4?v=${v}`} className="mt-3 w-40 aspect-[9/16] rounded-md border border-stone-200 bg-black" />
      </>}
      {car && <SchedulePosts kit={kit} reload={reload} />}
      </More>
    </div>
  );
};
